/**
Project: Voice Panel (c)
Title: Domain Setup Actions 
Description: Domain actions for get/modify domain records and regions
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import axios from "axios";
import { AppConfigProps } from "../../../../core/settings/app-config";
import {
  logMessage,
  checkIdentitySession,
  getSessionExpiredError,
} from "../../../../core/actions/common-actions";
import {
  DOMAIN_SETUP_PROGRESS_ACCOUNT_ID,
  DOMAIN_SETUP_PROGRESS_PAGE,
  DOMAIN_SETUP_PROGRESS_DOMAIN_REGIONS,
  DOMAIN_SETUP_PROCESS_DOMAIN_RECORD,
  DOMAIN_SETUP_PROCESS_UPDATE_DOMAIN_RECORD,
  DOMAIN_CREATE_USERS,
  DOMAIN_SETUP,
} from "../../../../store/voice-admin/constants";

const actionFileName = "Domain-setup-actions.js";

export function setDomainSetupProgressPage(pageNumber) {
  return async function (dispatch) {
    try {
      dispatch({
        type: DOMAIN_SETUP_PROGRESS_PAGE,
        payload: pageNumber,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function setDomainSetupProgressAccountNumber(accountNumber) {
  return async function (dispatch) {
    try {
      dispatch({
        type: DOMAIN_SETUP_PROGRESS_ACCOUNT_ID,
        payload: accountNumber,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function getDomainStep(cancelToken) {
  return async function (dispatch) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(AppConfigProps.serverRoutePrefix + "/domain-setup", cancelToken)
        .then((res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data &&
            res.data.result
          ) {
            dispatch({
              type: DOMAIN_SETUP,
              payload: res.data.result,
            });
          } else {
            dispatch({
              type: DOMAIN_SETUP,
              payload: null,
            });
          }
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getdomainDnsRecord()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            throw err && err.response ? err.response : err;
          }
        });
    } else {
      throw getSessionExpiredError();
    }
  };
}

export function getDomainRegions(cancelToken) {
  return async function (dispatch) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(AppConfigProps.serverRoutePrefix + "/domain-regions", cancelToken)
        .then((res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data &&
            res.data.records
          ) {
            dispatch({
              type: DOMAIN_SETUP_PROGRESS_DOMAIN_REGIONS,
              payload: res.data.records,
            });
          } else {
            dispatch({
              type: DOMAIN_SETUP_PROGRESS_DOMAIN_REGIONS,
              payload: null,
            });
          }
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getDomainRegions()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            throw err && err.response ? err.response : err;
          }
        });
    } else {
      throw getSessionExpiredError();
    }
  };
}

export function saveDomainRegions(domainRegions, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .post(
          AppConfigProps.serverRoutePrefix + `/domain-regions`,
          domainRegions,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">saveDomainRegions()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getDomainRecords(cancelToken) {
  return async function (dispatch) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(AppConfigProps.serverRoutePrefix + "/domain-records", cancelToken)
        .then((res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data &&
            res.data.records
          ) {
            dispatch({
              type: DOMAIN_SETUP_PROCESS_DOMAIN_RECORD,
              payload: res.data.records,
            });
          } else {
            dispatch({
              type: DOMAIN_SETUP_PROCESS_DOMAIN_RECORD,
              payload: null,
            });
          }
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getDomainRecords()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            throw err && err.response ? err.response : err;
          }
        });
    } else {
      throw getSessionExpiredError();
    }
  };
}

export function setUpdatedDomainRecords(domainName, textRecord) {
  return async function (dispatch) {
    try {
      dispatch({
        type: DOMAIN_SETUP_PROCESS_UPDATE_DOMAIN_RECORD,
        payload: { domainName, textRecord },
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function saveDomainRecords(domainRecord, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .post(
          AppConfigProps.serverRoutePrefix + `/domain-records`,
          domainRecord,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">saveDomainRecords()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getUsersRecord(cancelToken) {
  return async function (dispatch) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(AppConfigProps.serverRoutePrefix + "/domain-users", cancelToken)
        .then((res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data &&
            res.data.records
          ) {
            dispatch({
              type: DOMAIN_CREATE_USERS,
              payload: res.data.records,
            });
          } else {
            dispatch({
              type: DOMAIN_CREATE_USERS,
              payload: null,
            });
          }
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getdomainDnsRecord()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            throw err && err.response ? err.response : err;
          }
        });
    } else {
      throw getSessionExpiredError();
    }
  };
}

export function verifyUsers(users, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .post(
          AppConfigProps.serverRoutePrefix + `/domain-users`,
          users,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">verifyUsers()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}
